package com.karyadina.ui.screens.studyPlanner.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.MenuBook
import androidx.compose.material.icons.filled.*
import androidx.compose.material.icons.outlined.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.karyadina.model.StudySession
import com.karyadina.ui.theme.StudyStrings
import org.jetbrains.compose.resources.stringResource

@Composable
fun SessionSectionHeader(
    title: String,
    icon: ImageVector,
    iconColor: Color,
    count: Int
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        color = MaterialTheme.colorScheme.surface,
        shape = MaterialTheme.shapes.small
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp, horizontal = 16.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(icon, null, tint = iconColor, modifier = Modifier.size(18.dp))
            Text(title, style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
            Surface(shape = CircleShape, color = iconColor.copy(alpha = 0.2f)) {
                Text(count.toString(), Modifier.padding(horizontal = 8.dp, vertical = 2.dp), style = MaterialTheme.typography.labelSmall, color = iconColor)
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SessionCard(
    session: StudySession,
    isActive: Boolean,
    onStartSession: (String) -> Unit,
    onStopSession: (String) -> Unit,
    onDeleteClick: (String) -> Unit // <--- Added Callback
) {
    var expanded by remember { mutableStateOf(false) }
    var showDeleteDialog by remember { mutableStateOf(false) } // <--- Local State for Dialog

    // Logic: Remaining Time
    val progressPercentage = if (session.targetDurationMinutes > 0) {
        (session.actualDurationMinutes.toFloat() / session.targetDurationMinutes.toFloat() * 100f).coerceIn(0f, 100f)
    } else 0f

    val remainingMinutes = (session.targetDurationMinutes - session.actualDurationMinutes).coerceAtLeast(0)

    val progressColor = when {
        progressPercentage >= 100f -> MaterialTheme.colorScheme.tertiary
        progressPercentage >= 75f -> MaterialTheme.colorScheme.primary
        progressPercentage >= 50f -> MaterialTheme.colorScheme.secondary
        else -> MaterialTheme.colorScheme.surfaceVariant
    }

    val cardColor = if (isActive) MaterialTheme.colorScheme.primaryContainer else MaterialTheme.colorScheme.surface

    ElevatedCard(
        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
        colors = CardDefaults.elevatedCardColors(containerColor = cardColor),
        elevation = CardDefaults.elevatedCardElevation(defaultElevation = if (isActive) 1.dp else 4.dp)
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            // Active Indicator
            if (isActive) {
                Box(
                    Modifier.fillMaxWidth()
                        .height(3.dp)
                        .background(
                            Brush.horizontalGradient(
                                listOf(MaterialTheme.colorScheme.primary,
                                    MaterialTheme.colorScheme.secondary,
                                    MaterialTheme.colorScheme.tertiary
                                )
                            )
                        )
                )
            }

            // Header Row
            Row(Modifier.fillMaxWidth().padding(12.dp), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Column(Modifier.weight(1f)) {
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        Icon(Icons.AutoMirrored.Filled.MenuBook, null, tint = MaterialTheme.colorScheme.primary, modifier = Modifier.size(20.dp))
                        Text(session.subject, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                    }
                    if (session.topic.isNotBlank()) {
                        Text(session.topic, style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurface.copy(0.7f))
                    }

                    // Progress Bar
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.padding(top = 8.dp)) {
                        Box(
                            Modifier.weight(1f)
                                .height(8.dp)
                                .clip(MaterialTheme.shapes.small)
                                .background(
                                    MaterialTheme.colorScheme.surfaceVariant.copy(alpha = .5f))
                        ) {
                            Box(
                                Modifier
                                    .fillMaxWidth(progressPercentage / 100f)
                                    .fillMaxHeight()
                                    .clip(
                                        MaterialTheme.shapes.small)
                                    .background(
                                        Brush.horizontalGradient(
                                            colors = listOf(
                                                progressColor.copy(alpha = 0.7f),
                                                progressColor
                                            )
                                        )
                                    )
                            )
                        }
                        Text(
                            "${progressPercentage.toInt()}%",
                            style = MaterialTheme.typography.labelSmall,
                            color = progressColor,
                            fontWeight = FontWeight.Bold
                        )
                    }

                    Spacer(Modifier.height(6.dp))

                    // Badges
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        MetadataBadge(Icons.Filled.Schedule, "${session.actualDurationMinutes}/${session.targetDurationMinutes}min")
                        // Restored Remaining Time Badge
                        if (!session.completed && remainingMinutes > 0) MetadataBadge(Icons.Filled.HourglassEmpty, stringResource(StudyStrings.PlannerBadgeLeft, remainingMinutes))
                        if (session.completed) MetadataBadge(Icons.Filled.CheckCircle, stringResource(
                            StudyStrings.PlannerBadgeCompleted), MaterialTheme.colorScheme.primary)
                    }
                }

                // Actions
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    if (!session.completed) {
                        FilledTonalIconButton(
                            onClick = { if (isActive) onStopSession(session.id) else onStartSession(session.id) },
                            colors = IconButtonDefaults.filledTonalIconButtonColors(containerColor = if (isActive) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.primary)
                        ) {
                            Icon(if (isActive) Icons.Filled.Stop else Icons.Filled.PlayArrow, null, tint = Color.White)
                        }
                    }
                    IconButton(onClick = { expanded = !expanded }) {
                        Icon(if (expanded) Icons.Filled.ExpandLess else Icons.Filled.ExpandMore, null)
                    }
                }
            }

            // Expanded Details
            AnimatedVisibility(visible = expanded) {
                Column(Modifier.padding(16.dp)) {
                    HorizontalDivider()
                    Spacer(Modifier.height(8.dp))

                    DetailRow(Icons.Outlined.Schedule, stringResource(StudyStrings.PlannerCardTarget), "${session.targetDurationMinutes} min")
                    DetailRow(Icons.Outlined.Timer, stringResource(StudyStrings.PlannerCardSpent), "${session.actualDurationMinutes} min")
                    // Restored Remaining Time Detail Row
                    DetailRow(Icons.Outlined.Timelapse, stringResource(StudyStrings.PlannerCardRemaining), "$remainingMinutes min")

                    if (session.notes.isNotBlank()) {
                        Text(stringResource(StudyStrings.PlannerCardNotes), style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold, modifier = Modifier.padding(top=8.dp))
                        Text(session.notes, style = MaterialTheme.typography.bodySmall)
                    }

                    // Delete Button
                    if (!isActive) {
                        Spacer(Modifier.height(16.dp))
                        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                            IconButton(onClick = { showDeleteDialog = true }) {
                                Icon(Icons.Outlined.Delete, stringResource(StudyStrings.ActionDelete), tint = MaterialTheme.colorScheme.error)
                            }
                        }
                    }
                }
            }
        }
    }

    // Delete Confirmation Dialog
    if (showDeleteDialog) {
        AlertDialog(
            onDismissRequest = { showDeleteDialog = false },
            title = { Text(stringResource(StudyStrings.PlannerDialogDeleteTitle)) },
            text = { Text(stringResource(StudyStrings.PlannerDialogDeleteMsg)) },
            confirmButton = {
                Button(
                    onClick = {
                        onDeleteClick(session.id)
                        showDeleteDialog = false
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error)
                ) { Text(stringResource(StudyStrings.ActionDelete)) }
            },
            dismissButton = {
                TextButton(onClick = { showDeleteDialog = false }) { Text(stringResource(StudyStrings.Cancel)) }
            }
        )
    }
}

@Composable
fun MetadataBadge(icon: ImageVector, text: String, tint: Color = MaterialTheme.colorScheme.onSurfaceVariant) {
    Surface(shape = MaterialTheme.shapes.small, color = tint.copy(alpha = 0.1f)) {
        Row(Modifier.padding(horizontal = 6.dp, vertical = 4.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
            Icon(icon, null, Modifier.size(12.dp), tint = tint)
            Text(text, style = MaterialTheme.typography.labelSmall, color = tint)
        }
    }
}

@Composable
fun DetailRow(icon: ImageVector, label: String, value: String) {
    Row(Modifier.fillMaxWidth().padding(vertical = 4.dp), horizontalArrangement = Arrangement.SpaceBetween) {
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            Icon(icon, null, Modifier.size(16.dp), tint = MaterialTheme.colorScheme.primary)
            Text(label, style = MaterialTheme.typography.bodySmall)
        }
        Text(value, style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.Bold)
    }
}